import ApiService from '@/services/apiService';
import mixinMonitoringPanel from './mixinMonitoringPanel';
export default {
  name: 'BridgeMonitoringCharts',
  mixins: [mixinMonitoringPanel],
  props: {
    bridgeId: {
      type: Number,
      required: true
    },
    tenantId: {
      type: Number,
      required: true
    },
    timeRange: {
      type: Object,
      default: () => ({
        from: null,
        to: null
      })
    }
  },
  data() {
    return {
      isLoadingPackets: false,
      isLoadingStates: false,
      // state enum mapping (1-7)
      stateEnumMap: {
        1: 'Error',
        2: 'Unknown',
        3: 'Disabled',
        4: 'Unassigning',
        5: 'Assigning',
        6: 'Waiting',
        7: 'Active'
      },
      packetsChartData: {
        common: {
          labels: []
        },
        individual: [{
          label: 'Packet Count',
          data: [],
          color: '#00A9E1',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 3
        }, {
          label: 'Failed Packets',
          data: [],
          color: '#B71C1C',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 3
        }, {
          label: 'Average',
          data: [],
          color: 'rgba(13, 71, 161, 0.9)',
          borderWidth: 0.1,
          pointRadius: 0
        }, {
          label: 'Maximum',
          data: [],
          color: 'rgba(46, 125, 50, 0.25)',
          borderWidth: 0.1,
          pointRadius: 0
        }, {
          label: 'Minimum',
          data: [],
          color: 'rgba(158, 158, 158, 0.4)',
          borderWidth: 0.1,
          pointRadius: 0
        }]
      },
      statesChartData: {
        common: {
          labels: []
        },
        // prettier-ignore
        individual: [{
          label: 'Error (1)',
          data: [],
          color: 'rgba(183, 28, 28, 0.25)',
          borderWidth: 0.1,
          pointRadius: 0,
          order: 10
        }, {
          label: 'Unknown (2)',
          data: [],
          color: 'rgba(117, 117, 117, 0.25)',
          borderWidth: 0.1,
          pointRadius: 0,
          order: 10
        }, {
          label: 'Disabled (3)',
          data: [],
          color: 'rgba(97, 97, 97, 0.25)',
          borderWidth: 0.1,
          pointRadius: 0,
          order: 10
        }, {
          label: 'Unassigning (4)',
          data: [],
          color: 'rgba(230, 81, 0, 0.25)',
          borderWidth: 0.1,
          pointRadius: 0,
          order: 10
        }, {
          label: 'Assigning (5)',
          data: [],
          color: 'rgba(255, 160, 0, 0.25)',
          borderWidth: 0.1,
          pointRadius: 0,
          order: 10
        }, {
          label: 'Waiting (6)',
          data: [],
          color: 'rgba(25, 118, 210, 0.25)',
          borderWidth: 0.1,
          pointRadius: 0,
          order: 10
        }, {
          label: 'Active (7)',
          data: [],
          color: 'rgba(46, 125, 50, 0.25)',
          borderWidth: 0.1,
          pointRadius: 0,
          order: 10
        }, {
          label: 'Connector State',
          data: [],
          color: '#00A9E1',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 3,
          order: 1
        }]
      }
    };
  },
  computed: {
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: 'top'
        },
        elements: {
          line: {
            tension: 0
          },
          point: {
            radius: 3,
            hoverRadius: 5,
            hitRadius: 8
          }
        },
        scales: {
          xAxes: [{
            type: 'time',
            time: {
              displayFormats: {
                hour: 'HH:mm',
                minute: 'HH:mm'
              }
            },
            ticks: {
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 8
            }
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              precision: 0
            }
          }]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (tooltipItem, data) => {
              const datasetLabel = data.datasets[tooltipItem.datasetIndex].label || '';
              const value = tooltipItem.yLabel;
              return `${datasetLabel}: ${value}`;
            }
          }
        }
      };
    },
    stateChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true
        },
        scales: {
          xAxes: [{
            type: 'time',
            time: {
              displayFormats: {
                hour: 'HH:mm',
                minute: 'HH:mm'
              }
            },
            ticks: {
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 8
            }
          }],
          yAxes: [{
            ticks: {
              min: 1,
              max: 7,
              stepSize: 1,
              precision: 0,
              callback: value => {
                return this.stateEnumMap[value] || value;
              }
            }
          }]
        },
        elements: {
          line: {
            tension: 0,
            stepped: true
          },
          point: {
            radius: 4,
            hoverRadius: 6,
            hitRadius: 8
          }
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (tooltipItem, data) => {
              const datasetLabel = data.datasets[tooltipItem.datasetIndex].label || '';
              const stateValue = tooltipItem.yLabel;
              const stateName = this.stateEnumMap[stateValue] || stateValue;
              return `${datasetLabel}: ${stateName} (${stateValue})`;
            }
          }
        }
      };
    }
  },
  watch: {
    timeRange: {
      handler(newRange, oldRange) {
        // auto-refresh when time range changes
        if (newRange.from && newRange.to && (oldRange.from !== newRange.from || oldRange.to !== newRange.to)) {
          this.$nextTick(() => {
            this.fetchPacketsMetrics();
            this.fetchStatesMetrics();
          });
        }
      },
      deep: true
    }
  },
  mounted() {
    this.fetchPacketsMetrics();
    this.fetchStatesMetrics();
  },
  methods: {
    refresh() {
      // refresh charts with current time range
      this.fetchPacketsMetrics();
      this.fetchStatesMetrics();
    },
    async fetchPacketsMetrics() {
      this.isLoadingPackets = true;
      try {
        const start = this.timeRange.from ? this.timeRange.from : '-24h';
        const stop = this.timeRange.to ? this.timeRange.to : 'now()';
        const response = await ApiService.getBridgePacketMetrics(this.bridgeId, {
          start,
          stop,
          window: '5m' // 5-minute aggregation
        });
        this.packetsChartData = this.transformPacketsData(response);
      } catch (error) {
        console.error('Error fetching packet metrics:', error);
        this.packetsChartData = {
          common: {
            labels: []
          },
          individual: [{
            label: 'Packets',
            data: [],
            color: '#17bc00'
          }]
        };
      } finally {
        this.isLoadingPackets = false;
      }
    },
    async fetchStatesMetrics() {
      this.isLoadingStates = true;
      try {
        const start = this.timeRange.from ? this.timeRange.from : '-24h';
        const stop = this.timeRange.to ? this.timeRange.to : 'now()';
        const response = await ApiService.getBridgeStateMetrics(this.bridgeId, {
          start,
          stop
        });
        this.statesChartData = this.transformStatesData(response);
      } catch (error) {
        console.error('Error fetching state metrics:', error);
        this.statesChartData = {
          common: {
            labels: []
          },
          individual: [{
            label: 'State',
            data: [],
            color: '#ff9800'
          }]
        };
      } finally {
        this.isLoadingStates = false;
      }
    },
    transformPacketsData(response) {
      const dataArray = (response === null || response === void 0 ? void 0 : response.data) || [];
      if (!dataArray || dataArray.length === 0) {
        return {
          common: {
            labels: []
          },
          individual: [{
            label: 'Packet Count',
            data: [],
            color: '#00A9E1',
            borderWidth: 2,
            pointRadius: 2
          }, {
            label: 'Failed Packets',
            data: [],
            color: '#B71C1C',
            borderWidth: 2,
            pointRadius: 2
          }, {
            label: 'Average',
            data: [],
            color: 'rgba(13, 71, 161, 0.9)',
            borderWidth: 0.5,
            pointRadius: 0
          }, {
            label: 'Maximum',
            data: [],
            color: 'rgba(46, 125, 50, 0.25)',
            borderWidth: 0.5,
            pointRadius: 0
          }, {
            label: 'Minimum',
            data: [],
            color: 'rgba(158, 158, 158, 0.4)',
            borderWidth: 0.5,
            pointRadius: 0
          }]
        };
      }
      const labels = dataArray.map(item => new Date(item.time));
      const values = dataArray.map(item => ({
        x: new Date(item.time),
        y: item.count || 0
      }));
      const failedValues = dataArray.map(item => ({
        x: new Date(item.time),
        y: item.failedCount || 0
      }));

      // calculate min, max, avg for reference lines
      const counts = dataArray.map(item => item.count || 0);
      const min = Math.min(...counts);
      const max = Math.max(...counts);
      const avg = counts.reduce((sum, val) => sum + val, 0) / counts.length;
      const minLine = labels.map(time => ({
        x: time,
        y: min
      }));
      const maxLine = labels.map(time => ({
        x: time,
        y: max
      }));
      const avgLine = labels.map(time => ({
        x: time,
        y: avg
      }));
      return {
        common: {
          labels
        },
        individual: [{
          label: 'Packet Count',
          data: values,
          color: '#00A9E1',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 3
        }, {
          label: 'Failed Packets',
          data: failedValues,
          color: '#B71C1C',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 3,
          fill: false
        }, {
          label: 'Average',
          data: avgLine,
          color: 'rgba(13, 71, 161, 0.9)',
          borderWidth: 1,
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false
        }, {
          label: 'Maximum',
          data: maxLine,
          color: 'rgba(46, 125, 50, 0.25)',
          borderWidth: 0.1,
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false
        }, {
          label: 'Minimum',
          data: minLine,
          color: 'rgba(158, 158, 158, 0.4)',
          borderWidth: 0.1,
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false
        }]
      };
    },
    transformStatesData(response) {
      const dataArray = (response === null || response === void 0 ? void 0 : response.data) || [];
      if (!dataArray || dataArray.length === 0) {
        // prettier-ignore
        return {
          common: {
            labels: []
          },
          individual: [{
            label: 'Connector State',
            data: [],
            color: '#00A9E1',
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 3,
            order: 1
          }, {
            label: 'Active (7)',
            data: [],
            color: 'rgba(46, 125, 50, 0.25)',
            borderWidth: 0.1,
            pointRadius: 0,
            order: 10
          }, {
            label: 'Waiting (6)',
            data: [],
            color: 'rgba(25, 118, 210, 0.25)',
            borderWidth: 0.1,
            pointRadius: 0,
            order: 10
          }, {
            label: 'Assigning (5)',
            data: [],
            color: 'rgba(255, 160, 0, 0.25)',
            borderWidth: 0.1,
            pointRadius: 0,
            order: 10
          }, {
            label: 'Unassigning (4)',
            data: [],
            color: 'rgba(230, 81, 0, 0.25)',
            borderWidth: 0.1,
            pointRadius: 0,
            order: 10
          }, {
            label: 'Disabled (3)',
            data: [],
            color: 'rgba(97, 97, 97, 0.25)',
            borderWidth: 0.1,
            pointRadius: 0,
            order: 10
          }, {
            label: 'Unknown (2)',
            data: [],
            color: 'rgba(117, 117, 117, 0.25)',
            borderWidth: 0.1,
            pointRadius: 0,
            order: 10
          }, {
            label: 'Error (1)',
            data: [],
            color: 'rgba(183, 28, 28, 0.25)',
            borderWidth: 0.1,
            pointRadius: 0,
            order: 10
          }]
        };
      }
      const labels = dataArray.map(item => new Date(item.time));
      const values = dataArray.map(item => ({
        x: new Date(item.time),
        y: item.stateEnum || 0
      }));

      // horizontal reference lines for each state level
      const stateRefLines = [1, 2, 3, 4, 5, 6, 7].map(stateLevel => ({
        stateLevel,
        data: labels.map(time => ({
          x: time,
          y: stateLevel
        }))
      }));
      return {
        common: {
          labels
        },
        individual: [{
          label: 'Connector State',
          data: values,
          color: '#00A9E1',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 3,
          fill: false,
          order: 1
        }, {
          label: 'Active (7)',
          data: stateRefLines[6].data,
          color: 'rgba(46, 125, 50, 0.25)',
          borderWidth: 0.1,
          pointRadius: 0,
          fill: false,
          order: 10
        }, {
          label: 'Waiting (6)',
          data: stateRefLines[5].data,
          color: 'rgba(25, 118, 210, 0.25)',
          borderWidth: 0.1,
          pointRadius: 0,
          fill: false,
          order: 10
        }, {
          label: 'Assigning (5)',
          data: stateRefLines[4].data,
          color: 'rgba(255, 160, 0, 0.25)',
          borderWidth: 0.1,
          pointRadius: 0,
          fill: false,
          order: 10
        }, {
          label: 'Unassigning (4)',
          data: stateRefLines[3].data,
          color: 'rgba(230, 81, 0, 0.25)',
          borderWidth: 0.1,
          pointRadius: 0,
          fill: false,
          order: 10
        }, {
          label: 'Disabled (3)',
          data: stateRefLines[2].data,
          color: 'rgba(97, 97, 97, 0.25)',
          borderWidth: 0.1,
          pointRadius: 0,
          fill: false,
          order: 10
        }, {
          label: 'Unknown (2)',
          data: stateRefLines[1].data,
          color: 'rgba(117, 117, 117, 0.25)',
          borderWidth: 0.1,
          pointRadius: 0,
          fill: false,
          order: 10
        }, {
          label: 'Error (1)',
          data: stateRefLines[0].data,
          color: 'rgba(183, 28, 28, 0.25)',
          borderWidth: 0.1,
          pointRadius: 0,
          fill: false,
          order: 10
        }]
      };
    }
  }
};